import { BigNum } from '@drift-labs/sdk';
import React, { useState, useCallback } from 'react';
import { compareDriftProps } from 'src/utils/compareProps';
import { UIOrderType } from '@drift/common';
import TpSlOrderFormInputsV2 from 'src/components/TradeForm/TpSlOrderForm/TpSlOrderFormInputsV2';
import useTpSlOrderForm from 'src/components/TradeForm/TpSlOrderForm/hooks/useTpSlOrderForm';
import { SaveTpSlOrdersCallback } from 'src/components/TradeForm/TpSlOrderForm/types';

interface Props {
	direction: 'long' | 'short';
	entryPrice: BigNum;
	entrySize: BigNum;
	initialEntrySize?: BigNum;
	saveOrdersCallback: SaveTpSlOrdersCallback;
	takeProfitOrderType: UIOrderType;
	stopLossOrderType: UIOrderType;
	setTakeProfitOrderType: (orderType: UIOrderType) => void;
	setStopLossOrderType: (orderType: UIOrderType) => void;
	savedTakeProfitPrice: BigNum | null | undefined;
	savedStopLossPrice: BigNum | null | undefined;
	savedTakeProfitLimitPrice?: BigNum | null | undefined;
	savedStopLossLimitPrice?: BigNum | null | undefined;
	takeProfitOrderId?: number;
	stopLossOrderId?: number;
	takeProfitDisabled?: boolean;
	stopLossDisabled?: boolean;
	usePercentBasisDropdown?: boolean;
	marketIndex?: number;
	isStopLossBeyondLiquidationPriceProp?: boolean;
	allowTrailingStopLoss?: boolean;
	// Size input props for individual TP/SL size control
	partialPositionSelected?: boolean;
	stepSize: number;
	baseAssetSymbol?: string;
	markPrice: BigNum;
}

const TpSlOrderFormV2 = ({
	direction,
	entryPrice,
	entrySize,
	initialEntrySize,
	saveOrdersCallback,
	takeProfitOrderType,
	stopLossOrderType,
	setTakeProfitOrderType,
	setStopLossOrderType,
	savedTakeProfitPrice,
	savedStopLossPrice,
	savedTakeProfitLimitPrice,
	savedStopLossLimitPrice,
	takeProfitOrderId,
	stopLossOrderId,
	takeProfitDisabled = false,
	stopLossDisabled = false,
	usePercentBasisDropdown,
	marketIndex,
	isStopLossBeyondLiquidationPriceProp,
	allowTrailingStopLoss,
	// Size input props
	partialPositionSelected,
	stepSize,
	baseAssetSymbol,
	markPrice,
}: Props) => {
	// Individual order size state - each TP/SL input manages its own size
	const [takeProfitOrderSize, setTakeProfitOrderSize] =
		useState<BigNum>(entrySize);
	const [stopLossOrderSize, setStopLossOrderSize] = useState<BigNum>(entrySize);

	// Callback to handle size changes from individual input components
	const handleOrderSizeChange = useCallback(
		(isTakeProfit: boolean, size: BigNum) => {
			if (isTakeProfit) {
				setTakeProfitOrderSize(size);
			} else {
				setStopLossOrderSize(size);
			}
		},
		[]
	);

	// Wrapped callback that includes order sizes
	const wrappedSaveOrdersCallback: SaveTpSlOrdersCallback = useCallback(
		(orders) => {
			if (orders?.takeProfit || orders?.stopLoss) {
				// Add order sizes to the bracket orders
				const enhancedOrders = {
					takeProfit: orders.takeProfit
						? (() => {
								// Only check for duplicates when entire position tab is selected
								if (!partialPositionSelected) {
									// Check if existing TP order covers entire position with same price - if so, don't send it

									const inputPriceMatchesSavedPrice =
										orders.takeProfit.price &&
										savedTakeProfitPrice &&
										orders.takeProfit.price.eq(savedTakeProfitPrice);

									// Only check limit price if both orders have limit prices
									const inputLimitPriceMatchesSavedPrice =
										!orders.takeProfit.limitPrice && !savedTakeProfitLimitPrice
											? true // Both are market orders
											: orders.takeProfit.limitPrice &&
												savedTakeProfitLimitPrice &&
												orders.takeProfit.limitPrice.eq(
													savedTakeProfitLimitPrice
												);

									if (
										inputPriceMatchesSavedPrice &&
										inputLimitPriceMatchesSavedPrice
									) {
										return undefined; // Don't send this order to prevent duplicates
									}
								}
								return {
									...orders.takeProfit,
									...(partialPositionSelected && { size: takeProfitOrderSize }),
								};
							})()
						: undefined,
					stopLoss: orders.stopLoss
						? (() => {
								// Only check for duplicates when entire position tab is selected
								if (!partialPositionSelected) {
									// Check if existing SL order covers entire position with same price - if so, don't send it

									const inputPriceMatchesSavedPrice =
										orders.stopLoss.price &&
										savedStopLossPrice &&
										orders.stopLoss.price.eq(savedStopLossPrice);

									// Only check limit price if both orders have limit prices
									const inputLimitPriceMatchesSavedPrice =
										!orders.stopLoss.limitPrice && !savedStopLossLimitPrice
											? true // Both are market orders
											: orders.stopLoss.limitPrice &&
												savedStopLossLimitPrice &&
												orders.stopLoss.limitPrice.eq(savedStopLossLimitPrice);

									if (
										inputPriceMatchesSavedPrice &&
										inputLimitPriceMatchesSavedPrice
									) {
										return undefined; // Don't send this order to prevent duplicates
									}
								}

								return {
									...orders.stopLoss,
									...(partialPositionSelected && { size: stopLossOrderSize }),
								};
							})()
						: undefined,
					// Preserve the anyInvalid flag from the original orders
					anyInvalid: orders.anyInvalid,
				};
				saveOrdersCallback(enhancedOrders);
			} else {
				saveOrdersCallback(orders);
			}
		},
		[
			saveOrdersCallback,
			takeProfitOrderSize,
			stopLossOrderSize,
			takeProfitOrderId,
			stopLossOrderId,
			entrySize,
			savedTakeProfitPrice,
			savedStopLossPrice,
			savedTakeProfitLimitPrice,
			savedStopLossLimitPrice,
			partialPositionSelected,
		]
	);

	// Use the hook with take profit order type
	const {
		takeProfitPriceStringValue,
		takeProfitPercentStringValue,
		takeProfitPrice,
		takeProfitLimitPrice,
		takeProfitLimitPriceStringValue,
		takeProfitInvalid,
		takeProfitInvalidReason,
		stopLossPriceStringValue,
		stopLossPercentStringValue,
		stopLossPrice,
		stopLossLimitPrice,
		stopLossLimitPriceStringValue,
		stopLossInvalid,
		stopLossInvalidReason,
		updatePrice,
		updatePercent,
		updateLimitPrice,
		updateOrders,
		formDisabled,
		triggerOrderDirection,
		baseOrderDirection,
		selectedMarketId,
		isStopLossBeyondLiquidationPrice,
		positionIsLong,
	} = useTpSlOrderForm({
		direction,
		entryPrice,
		entrySize,
		saveOrdersCallback: wrappedSaveOrdersCallback,
		savedTakeProfitPrice,
		savedStopLossPrice,
		savedTakeProfitLimitPrice,
		savedStopLossLimitPrice,
		marketIndex,
		allowTrailingStopLoss,
	});

	return (
		<>
			<TpSlOrderFormInputsV2
				isTakeProfit
				baseOrderDirection={baseOrderDirection}
				triggerOrderDirection={triggerOrderDirection}
				triggerPrice={takeProfitPrice}
				triggerPriceStringValue={takeProfitPriceStringValue}
				triggerPercentStringValue={takeProfitPercentStringValue}
				limitPrice={takeProfitLimitPrice}
				limitPriceStringValue={takeProfitLimitPriceStringValue}
				updatePrice={updatePrice}
				updatePercent={updatePercent}
				updateLimitPrice={updateLimitPrice}
				updateOrders={updateOrders}
				entrySize={
					takeProfitDisabled ? initialEntrySize || new BigNum(0) : entrySize
				}
				entryPrice={entryPrice}
				selectedMarketId={selectedMarketId}
				orderType={takeProfitOrderType}
				setOrderType={setTakeProfitOrderType}
				disabled={formDisabled || takeProfitDisabled}
				orderId={takeProfitOrderId}
				usePercentBasisDropdown={usePercentBasisDropdown}
				formDisabled={formDisabled}
				invalid={takeProfitInvalid}
				invalidReason={takeProfitInvalidReason}
				positionIsLong={positionIsLong}
				// Size input props for individual TP/SL control
				partialPositionSelected={partialPositionSelected}
				stepSize={stepSize}
				baseAssetSymbol={baseAssetSymbol}
				markPrice={markPrice}
				onOrderSizeChange={handleOrderSizeChange}
				// Saved order values for restoring prices
				savedTriggerPrice={savedTakeProfitPrice}
				savedLimitPrice={savedTakeProfitLimitPrice}
			/>
			<TpSlOrderFormInputsV2
				isTakeProfit={false}
				baseOrderDirection={baseOrderDirection}
				triggerOrderDirection={triggerOrderDirection}
				triggerPrice={stopLossPrice}
				triggerPriceStringValue={stopLossPriceStringValue}
				triggerPercentStringValue={stopLossPercentStringValue}
				limitPrice={stopLossLimitPrice}
				limitPriceStringValue={stopLossLimitPriceStringValue}
				updatePrice={updatePrice}
				updatePercent={updatePercent}
				updateLimitPrice={updateLimitPrice}
				updateOrders={updateOrders}
				entrySize={
					stopLossDisabled ? initialEntrySize || new BigNum(0) : entrySize
				}
				entryPrice={entryPrice}
				selectedMarketId={selectedMarketId}
				orderType={stopLossOrderType}
				setOrderType={setStopLossOrderType}
				disabled={formDisabled || stopLossDisabled}
				orderId={stopLossOrderId}
				usePercentBasisDropdown={usePercentBasisDropdown}
				formDisabled={formDisabled}
				invalid={stopLossInvalid}
				invalidReason={stopLossInvalidReason}
				positionIsLong={positionIsLong}
				isStopLossBeyondLiquidationPrice={
					isStopLossBeyondLiquidationPriceProp ||
					isStopLossBeyondLiquidationPrice
				}
				// Size input props for individual TP/SL control
				partialPositionSelected={partialPositionSelected}
				stepSize={stepSize}
				baseAssetSymbol={baseAssetSymbol}
				markPrice={markPrice}
				onOrderSizeChange={handleOrderSizeChange}
				// Saved order values for restoring prices
				savedTriggerPrice={savedStopLossPrice}
				savedLimitPrice={savedStopLossLimitPrice}
			/>
		</>
	);
};

export default React.memo(TpSlOrderFormV2, compareDriftProps);
